import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  empresasPlataforma,
  type EmpresaPlataforma,
} from '@/mocks/backoffice/empresas';
import {
  getStatusColor,
  getStatusLabel,
  getPlanoLabel,
} from '@/mocks/backoffice/empresasHelpers';

export default function SelecionarEmpresaConfigPage() {
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | EmpresaPlataforma['status']>('todos');

  const empresasFiltradas = useMemo(() => {
    return empresasPlataforma.filter((e) => {
      const matchBusca =
        !busca ||
        e.nome_fantasia.toLowerCase().includes(busca.toLowerCase()) ||
        e.razao_social.toLowerCase().includes(busca.toLowerCase()) ||
        e.cnpj.includes(busca) ||
        e.id.toLowerCase().includes(busca.toLowerCase());
      const matchStatus = filtroStatus === 'todos' || e.status === filtroStatus;
      return matchBusca && matchStatus;
    });
  }, [busca, filtroStatus]);

  const stats = useMemo(() => {
    const ativas = empresasPlataforma.filter((e) => e.status === 'ativa').length;
    const pendentes = empresasPlataforma.filter((e) => e.status === 'pendente').length;
    return { ativas, pendentes, total: empresasPlataforma.length };
  }, []);

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Configurações das Empresas</h1>
            <p className="text-sm text-gray-500 mt-1">
              Selecione uma empresa para gerenciar suas configurações de plataforma, campanhas e premiados
            </p>
          </div>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm"></i>
              </div>
              <span>Nova Empresa</span>
            </div>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {[
            { label: 'Ativas', value: stats.ativas, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'ri-checkbox-circle-line' },
            { label: 'Pendentes', value: stats.pendentes, color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-time-line' },
            { label: 'Total', value: stats.total, color: 'text-gray-800', bg: 'bg-gray-50', icon: 'ri-building-line' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} ${s.color} text-lg`}></i>
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar por nome, CNPJ ou ID..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value as typeof filtroStatus)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              <option value="todos">Todos os status</option>
              <option value="ativa">Ativa</option>
              <option value="pendente">Pendente</option>
              <option value="suspensa">Suspensa</option>
              <option value="inativa">Inativa</option>
            </select>
          </div>
        </div>

        {/* Grid de Empresas */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {empresasFiltradas.map((empresa) => (
            <div
              key={empresa.id}
              className="bg-white rounded-xl border border-gray-100 p-5 hover:border-emerald-300 hover:shadow-sm transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-building-2-line text-stone-500 text-lg"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">{empresa.nome_fantasia}</h3>
                    <p className="text-[11px] text-gray-400">{empresa.cnpj}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusColor(empresa.status)}`}>
                  {getStatusLabel(empresa.status)}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Plano</span>
                  <span className="font-bold text-gray-700">{getPlanoLabel(empresa.plano)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Participantes</span>
                  <span className="font-bold text-gray-700">
                    {empresa.participantes_ativos} / {empresa.participantes_total}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Saldo</span>
                  <span className="font-bold text-gray-700">
                    R$ {empresa.saldo_conta_digital.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to={`/backoffice/empresas/${empresa.id}?tab=config`}
                  className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
                >
                  <div className="flex items-center justify-center space-x-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-equalizer-line text-sm"></i>
                    </div>
                    <span>Configurar</span>
                  </div>
                </Link>
                <Link
                  to={`/backoffice/empresas/${empresa.id}`}
                  className="px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
                >
                  <div className="flex items-center justify-center space-x-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-sm"></i>
                    </div>
                    <span>Detalhes</span>
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {empresasFiltradas.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-building-line text-3xl text-gray-300"></i>
            </div>
            <p className="text-sm text-gray-400">Nenhuma empresa encontrada com os filtros selecionados.</p>
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
}